import { PANEL_PRODUCT_CELL_TYPE_LABELS, PANEL_PRODUCT_CELL_TYPE_KOREAN_LABELS } from '@/constants/panelProduct';
import i18n from '@/plugins/i18n';

function localizedCellTypeDetail(code) {
  const key = `panelProductCrud.cellTypeDetail.${code}`;
  if (i18n.te(key)) {
    return i18n.t(key);
  }
  return PANEL_PRODUCT_CELL_TYPE_KOREAN_LABELS[code] || '';
}

/**
 * 패널 제품 관련 유틸리티 함수들
 */

/**
 * 셀 타입 코드를 라벨로 변환 (기술 약어 + 로케일 설명)
 * @param {number|string} cellType - 셀 타입 코드 (숫자 또는 문자열)
 * @returns {string} 셀 타입 라벨
 */
export function getCellTypeLabel(cellType) {
  if (!cellType) return '-';

  const code = typeof cellType === 'number' ? cellType : parseInt(cellType, 10);
  const englishLabel = PANEL_PRODUCT_CELL_TYPE_LABELS[cellType] ?? PANEL_PRODUCT_CELL_TYPE_LABELS[code];
  const detail = Number.isFinite(code) ? localizedCellTypeDetail(code) : '';

  if (englishLabel && detail) {
    return `${englishLabel} (${detail})`;
  }
  if (englishLabel) {
    return englishLabel;
  }
  return String(cellType);
}

/**
 * 패널 제품의 크기 정보를 포맷팅
 * @param {number} width - 너비 (mm)
 * @param {number} height - 높이 (mm)
 * @param {number} thickness - 두께 (mm)
 * @returns {string} 포맷팅된 크기 문자열
 */
export function formatDimensions(width, height, thickness) {
  if (width && height && thickness) {
    return `${width}mm x ${height}mm x ${thickness}mm`;
  } else if (width && height) {
    return `${width}mm x ${height}mm`;
  } else if (width) {
    return `${width}mm`;
  }
  return '-';
}

/**
 * 전력 출력을 포맷팅
 * @param {number} power - 전력 (W)
 * @returns {string} 포맷팅된 전력 문자열
 */
export function formatPowerOutput(power) {
  if (!power) return '-';
  return `${power}W`;
}


/**
 * 온도 범위를 포맷팅
 * @param {number} minTemp - 최소 온도 (°C)
 * @param {number} maxTemp - 최대 온도 (°C)
 * @returns {string} 포맷팅된 온도 범위 문자열
 */
export function formatTemperatureRange(minTemp, maxTemp) {
  if (minTemp && maxTemp) {
    return `${minTemp}°C ~ ${maxTemp}°C`;
  } else if (minTemp) {
    return `${minTemp}°C ~`;
  } else if (maxTemp) {
    return `~ ${maxTemp}°C`;
  }
  return '-';
}


/**
 * 패널 제품 데이터를 API 요청 형식으로 변환
 * @param {Object} panelProduct - 패널 제품 데이터
 * @returns {Object} API 요청용 데이터
 */
export function formatPanelProductForApi(panelProduct) {
  return {
    modelName: panelProduct.modelName,
    maker: panelProduct.maker,
    maxPowerOutput: panelProduct.maxPowerOutput,
    width: panelProduct.width,
    height: panelProduct.height,
    panelProductCellType: panelProduct.panelProductCellType,
    maxVoltage: panelProduct.maxVoltage,
    maxCurrent: panelProduct.maxCurrent,
    openCircuitVoltage: panelProduct.openCircuitVoltage,
    shortCircuitCurrent: panelProduct.shortCircuitCurrent,
    weight: panelProduct.weight,
    thickness: panelProduct.thickness,
    warrantyPeriodYears: panelProduct.warrantyPeriodYears,
    numberOfCells: panelProduct.numberOfCells,
    cellEfficiency: panelProduct.cellEfficiency,
    operatingTempMin: panelProduct.operatingTempMin,
    operatingTempMax: panelProduct.operatingTempMax,
    ipRating: panelProduct.ipRating
  };
}

/**
 * API 응답 데이터를 프론트엔드 형식으로 변환
 * @param {Object} apiData - API 응답 데이터
 * @returns {Object} 프론트엔드용 데이터
 */
export function formatPanelProductFromApi(apiData) {
  return {
    seq: apiData.seq,
    modelName: apiData.modelName,
    maker: apiData.maker,

    maxPowerOutput: apiData.maxPowerOutput,
    width: apiData.width,
    height: apiData.height,
    panelProductCellType: apiData.panelProductCellType,
    maxVoltage: apiData.maxVoltage,
    maxCurrent: apiData.maxCurrent,
    openCircuitVoltage: apiData.openCircuitVoltage,
    shortCircuitCurrent: apiData.shortCircuitCurrent,
    weight: apiData.weight,
    thickness: apiData.thickness,
    warrantyPeriodYears: apiData.warrantyPeriodYears,
    numberOfCells: apiData.numberOfCells,
    cellEfficiency: apiData.cellEfficiency,
    operatingTempMin: apiData.operatingTempMin,
    operatingTempMax: apiData.operatingTempMax,
    ipRating: apiData.ipRating
  };
}

const V = 'panelProductCrud.validation';

/**
 * 패널 제품 데이터 유효성 검사
 * @param {Object} panelProduct - 검사할 패널 제품 데이터
 * @returns {Object} 검사 결과 { isValid: boolean, errors: string[] } — errors는 vue-i18n 키
 */
export function validatePanelProduct(panelProduct) {
  const errors = [];

  if (!panelProduct.modelName) {
    errors.push(`${V}.modelRequired`);
  }

  if (!panelProduct.maker) {
    errors.push(`${V}.makerRequired`);
  }

  if (panelProduct.maxPowerOutput !== null && panelProduct.maxPowerOutput <= 0) {
    errors.push(`${V}.maxPowerPositive`);
  }

  if (panelProduct.width !== null && panelProduct.width <= 0) {
    errors.push(`${V}.widthPositive`);
  }

  if (panelProduct.height !== null && panelProduct.height <= 0) {
    errors.push(`${V}.heightPositive`);
  }

  if (panelProduct.numberOfCells !== null && (panelProduct.numberOfCells < 1 || panelProduct.numberOfCells > 10000)) {
    errors.push(`${V}.numberOfCellsRange`);
  }

  if (panelProduct.cellEfficiency !== null && (panelProduct.cellEfficiency < 0 || panelProduct.cellEfficiency > 100)) {
    errors.push(`${V}.cellEfficiencyRange`);
  }

  if (panelProduct.operatingTempMin !== null && (panelProduct.operatingTempMin < -50 || panelProduct.operatingTempMin > 100)) {
    errors.push(`${V}.operatingTempMinRange`);
  }

  if (panelProduct.operatingTempMax !== null && (panelProduct.operatingTempMax < -50 || panelProduct.operatingTempMax > 100)) {
    errors.push(`${V}.operatingTempMaxRange`);
  }

  if (panelProduct.ipRating && !/^IP[0-9]{2}$/.test(panelProduct.ipRating)) {
    errors.push(`${V}.ipRatingFormat`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * 셀 타입 옵션을 생성 (영문/한국어 모두 표시)
 * @param {Object} cellTypes - 백엔드에서 받은 셀 타입 데이터 (Map<Integer, String> 형태)
 * @returns {Array} select 컴포넌트용 옵션 배열
 */
export function createCellTypeOptions(cellTypes) {
  if (!cellTypes || typeof cellTypes !== 'object') {
    return [];
  }

  // 백엔드에서 Map<Integer, String> 형태로 오므로
  // key는 숫자(코드), value는 문자열(라벨)
  const result = Object.entries(cellTypes).map(([code, label]) => {
    const codeNum = parseInt(code, 10);
    const detail = Number.isFinite(codeNum) ? localizedCellTypeDetail(codeNum) : '';
    return {
      value: codeNum,
      label: detail ? `${label} (${detail})` : label
    };
  });
  console.log('result:', result);
  return result;
}
